<script setup lang="ts">
import type { InteractionRequest } from 'eve/events'

import { cn } from 'cn'
import { APPROVE_OPTION_ID, DENY_OPTION_ID } from 'eve/events'

import type { ParametersGetAll } from '~/composables/api/document/useFetchDocument'

const props = defineProps<{
  disabled?: boolean
  request: InteractionRequest
  interactionId: string
  response?: {
    optionId?: string
    text?: string
  }
  toolInput: unknown
  toolName: string
}>()

const emit = defineEmits<{
  answer: [response: {
    optionId?: string
    interactionId: string
    text?: string
  }]
}>()

const { t } = useI18n()
const { activeWorkspaceId } = useAuth()
const confirmation = computed(() => docueAgentToolConfirmation(props.toolName))
const input = computed(() => docueAgentToolInput(props.toolInput))

function inputString(key: string): string {
  const value = input.value[key]
  return typeof value === 'string' ? value.trim() : ''
}

const toolKind = computed(() => confirmation.value?.target)
const action = computed(() => confirmation.value?.action)
const documentId = computed(() => inputString('document_id'))
const folderId = computed(() => inputString('folder_id'))
const draftContractId = computed(() => inputString('contract_id'))
const isCurrentWorkspace = computed(() => inputString('workspace_id') === activeWorkspaceId.value)

const needsDocument = computed(() => isCurrentWorkspace.value && toolKind.value === 'document' && !!documentId.value)
const needsDraft = computed(() => isCurrentWorkspace.value && toolKind.value === 'draft' && !!draftContractId.value)
const needsFolderContents = computed(() =>
  isCurrentWorkspace.value && action.value === 'delete-folder' && !!folderId.value,
)

const documentQuery = useQueryDocument.byId({
  id: documentId,
  useQueryOptions: { enabled: needsDocument },
})

const draftQuery = useQueryDocument.byContractId({
  contractId: draftContractId,
  useQueryOptions: { enabled: needsDraft },
})

const folderQuery = useQueryFolder.byId({
  id: folderId,
  useQueryOptions: { enabled: needsFolderContents },
})

// Match platform-mcp loadFolderDeletionContext: ancestor filter + meta.total
const folderDocumentCountParams = computed<ParametersGetAll>(() => ({
  page: 1,
  per_page: 1,
  sort: '-document.created_at',
  filters: folderId.value
    ? [{
        clause: 'where_any',
        rules: [{
          entity: 'document.folder.ancestor',
          conditions: [{
            field: 'id',
            operator: 'is_any_of',
            value: [folderId.value],
          }],
        }],
      }]
    : [],
}))

const folderDocumentCountQuery = useQueryDocument.all({
  params: folderDocumentCountParams,
  useQueryOptions: {
    enabled: needsFolderContents,
  },
})

const folderDocumentCount = computed(() =>
  folderDocumentCountQuery.data.value?.meta.total,
)

const target = computed(() => {
  if (toolKind.value === 'document' || toolKind.value === 'draft') {
    const document = toolKind.value === 'draft' ? draftQuery.data.value : documentQuery.data.value
    return {
      href: document ? `/2/documents/d/${encodeURIComponent(document.id)}` : undefined,
      icon: 'docue:file-05',
      label: t('docueAgent.confirmation.document'),
      name: document?.title?.trim() || t('docueAgent.confirmation.document'),
      openLabel: t('docueAgent.confirmation.open-document'),
    }
  }

  if (toolKind.value === 'folder') {
    const isDeleting = action.value === 'delete-folder'
    return {
      href: folderId.value ? `/2/documents/f/${encodeURIComponent(folderId.value)}` : undefined,
      icon: 'docue:folder',
      label: t('docueAgent.confirmation.folder'),
      name: (isDeleting ? folderQuery.data.value?.name : undefined) || folderId.value || t('docueAgent.confirmation.folder'),
      openLabel: t('docueAgent.confirmation.open-folder'),
    }
  }

  return undefined
})

const isLoadingTarget = computed(() => (needsDocument.value && documentQuery.isLoading.value)
  || (needsDraft.value && draftQuery.isLoading.value)
  || (needsFolderContents.value && (folderQuery.isLoading.value || folderDocumentCountQuery.isLoading.value)))

const isTargetAvailable = computed(() => {
  if (!confirmation.value)
    return true
  if (!isCurrentWorkspace.value)
    return false
  if (toolKind.value === 'draft')
    return !!draftContractId.value && !!draftQuery.data.value
  if (toolKind.value === 'document')
    return !!documentId.value && !!documentQuery.data.value
  return !!folderId.value && (action.value !== 'delete-folder'
    || (!!folderQuery.data.value && typeof folderDocumentCount.value === 'number'))
})

const details = computed(() => docueAgentConfirmationDetails(props.toolName, props.toolInput, t))

const hasTargetError = computed(() =>
  !isLoadingTarget.value && !isTargetAvailable.value,
)

const copy = computed(() => {
  if (action.value === 'delete-folder') {
    const count = folderDocumentCount.value
    return {
      action: t('docueAgent.confirmation.delete-folder'),
      description: typeof count === 'number'
        ? t('docueAgent.confirmation.delete-folder-description', { count })
        : undefined,
      title: t('docueAgent.confirmation.delete-folder-title'),
    }
  }

  if (confirmation.value) {
    return {
      action: t(`docueAgent.confirmation.${action.value}`),
      description: t(`docueAgent.confirmation.${action.value}-description`),
      title: t(`docueAgent.confirmation.${action.value}-title`),
    }
  }

  return {
    action: t('docueAgent.confirmation.allow'),
    description: props.request.prompt,
    title: props.request.title ?? t('docueAgent.confirmation.generic'),
  }
})

const isAnswered = computed(() =>
  props.response?.optionId !== undefined
  || !!props.response?.text?.trim(),
)

const approveOption = computed(() =>
  props.request.options?.find(option => option.id === APPROVE_OPTION_ID),
)
const cancelOption = computed(() =>
  props.request.options?.find(option => option.id === DENY_OPTION_ID),
)

function isSelectedOption(optionId: string) {
  return props.response?.optionId === optionId
}

function isOptionDisabled(optionId: string) {
  if (props.disabled || isAnswered.value)
    return true

  return optionId === APPROVE_OPTION_ID && !!confirmation.value && (isLoadingTarget.value || hasTargetError.value)
}

function answerWithOption(optionId: string) {
  if (isOptionDisabled(optionId))
    return

  emit('answer', {
    optionId,
    interactionId: props.interactionId,
  })
}
</script>

<template>
  <section
    class="overflow-hidden rounded-xl border border-neutral-200 bg-white"
    :aria-labelledby="`docue-agent-confirmation-${interactionId}`"
  >
    <div
      class="space-y-3.5 p-4"
    >
      <div class="space-y-1">
        <h3
          :id="`docue-agent-confirmation-${interactionId}`"
          class="text-sm font-semibold leading-5 text-neutral-900"
        >
          {{ copy.title }}
        </h3>
        <p
          v-if="copy.description"
          class="text-sm leading-5 text-neutral-600"
        >
          {{ copy.description }}
        </p>
      </div>

      <div
        v-if="target || hasTargetError"
        class="rounded-lg bg-neutral-50 px-3 py-2.5"
      >
        <div
          v-if="isLoadingTarget"
          class="space-y-2"
          role="status"
          :aria-label="t('docueAgent.confirmation.loading-target')"
        >
          <div class="h-3 w-16 animate-pulse rounded bg-neutral-200" />
          <div class="h-4 w-3/4 animate-pulse rounded bg-neutral-200" />
        </div>

        <p
          v-else-if="hasTargetError"
          class="text-sm leading-5 text-red-700"
          role="alert"
        >
          {{ t('docueAgent.confirmation.target-verification-failed') }}
        </p>

        <div v-else-if="target">
          <p class="text-xs font-medium text-neutral-500">
            {{ target.label }}
          </p>
          <NuxtLink
            v-if="target.href"
            :to="target.href"
            class="
              mt-0.5 inline-flex max-w-full items-center gap-1.5 text-sm
              font-medium text-neutral-900 underline-offset-2 hover:underline
              focus-visible:rounded-sm focus-visible:outline-none
              focus-visible:ring-2 focus-visible:ring-neutral-400
            "
            :aria-label="target.openLabel"
          >
            <UiIcon
              :name="target.icon"
              aria-hidden="true"
              class="size-4 shrink-0 text-neutral-500"
            />
            <span class="break-words">
              {{ target.name }}
            </span>
          </NuxtLink>
          <p
            v-else
            class="mt-0.5 break-words text-sm font-medium text-neutral-900"
          >
            {{ target.name }}
          </p>
        </div>
      </div>

      <template v-if="confirmation && !isLoadingTarget && !hasTargetError">
        <dl
          v-if="details.fields.length"
          class="space-y-2 text-sm"
        >
          <div
            v-for="field in details.fields"
            :key="field.label"
          >
            <dt class="text-xs font-medium text-neutral-500">
              {{ field.label }}
            </dt>
            <dd class="whitespace-pre-wrap break-words text-neutral-900">
              {{ field.value }}
            </dd>
          </div>
        </dl>
        <div v-if="details.recipients.length">
          <p class="mb-2 text-xs font-medium text-neutral-500">
            {{ t('docueAgent.confirmation.signees') }}
          </p>
          <ul class="space-y-2">
            <li
              v-for="(recipient, index) in details.recipients"
              :key="index"
              class="rounded-lg bg-neutral-50 px-3 py-2.5"
            >
              <p class="break-words text-sm font-medium text-neutral-900">
                {{ recipient.name }}
              </p>
              <p
                v-if="recipient.contacts.length"
                class="mt-0.5 break-words text-xs text-neutral-600"
              >
                {{ recipient.contacts.join(' · ') }}
              </p>
            </li>
          </ul>
        </div>
        <p
          v-for="notice in details.notices"
          :key="notice"
          class="text-sm leading-5 text-neutral-600"
        >
          {{ notice }}
        </p>
      </template>
    </div>

    <div
      v-if="approveOption || cancelOption"
      :class="cn(
        'flex gap-2 border-t border-neutral-200 bg-neutral-50 px-4 py-3',
        'flex-col-reverse md:flex-row md:justify-end',
      )"
      role="group"
      :aria-labelledby="`docue-agent-confirmation-${interactionId}`"
    >
      <UiButton
        v-if="cancelOption"
        type="button"
        color="neutral"
        variant="outline"
        class="h-11 w-full md:h-9 md:w-auto"
        :selected="isSelectedOption(cancelOption.id)"
        :disabled="isOptionDisabled(cancelOption.id)"
        :aria-pressed="isSelectedOption(cancelOption.id)"
        @click="answerWithOption(cancelOption.id)"
      >
        {{ t('docueAgent.confirmation.cancel') }}
      </UiButton>
      <UiButton
        v-if="approveOption"
        type="button"
        class="h-11 w-full md:h-9 md:w-auto"
        :color="confirmation?.destructive ? 'destructive' : 'primary'"
        :selected="isSelectedOption(approveOption.id)"
        :loading="isLoadingTarget && !isAnswered"
        :disabled="isOptionDisabled(approveOption.id)"
        :aria-pressed="isSelectedOption(approveOption.id)"
        @click="answerWithOption(approveOption.id)"
      >
        {{ copy.action }}
      </UiButton>
    </div>
  </section>
</template>
